import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectorRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface FilterOption {
  label: string;
  value: any;
  [key: string]: any;
}

@Component({
  selector: 'app-select-filter',
  templateUrl: './app-select-filter.component.html',
  styleUrls: ['./app-select-filter.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppSelectFilterComponent),
      multi: true,
    },
  ],
})
export class AppSelectFilterComponent implements ControlValueAccessor {
  @Input() labelText: string = 'Filter';
  @Input() label: string = '';

  get resolvedLabel(): string {
    return this.label || this.labelText || 'Filter';
  }
  @Input() options: FilterOption[] = [];
  @Input() placeholder: string = 'Select option';
  @Input() searchPlaceholder: string = 'Search...';
  @Input() searchable: boolean = false;
  @Input() multiple: boolean = false;
  @Input() disabled: boolean = false;

  @Output() filterChange = new EventEmitter<any>();
  @Output() clear = new EventEmitter<void>();

  selectedValue: any = null;
  tempSelectedValue: any = null;
  isOpen: boolean = false;
  searchText: string = '';

  private onChange: (val: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  writeValue(val: any): void {
    if (this.multiple) {
      this.selectedValue = Array.isArray(val) ? [...val] : (val !== undefined && val !== null && val !== '' ? [val] : []);
      this.tempSelectedValue = [...this.selectedValue];
    } else {
      this.selectedValue = val !== undefined && val !== null ? val : null;
      this.tempSelectedValue = this.selectedValue;
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  get isFilterApplied(): boolean {
    if (this.multiple) {
      return Array.isArray(this.selectedValue) && this.selectedValue.length > 0;
    }
    return (
      this.selectedValue !== null &&
      this.selectedValue !== undefined &&
      this.selectedValue !== ''
    );
  }

  get displaySelectedLabel(): string {
    if (!this.isFilterApplied) return '';
    if (this.multiple && Array.isArray(this.selectedValue)) {
      const labels = this.selectedValue.map((val) => {
        const match = this.options.find((opt) => opt.value === val);
        return match ? match.label : String(val);
      });
      if (labels.length <= 2) {
        return labels.join(', ');
      }
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
    }
    const match = this.options.find((opt) => opt.value === this.selectedValue);
    return match ? match.label : String(this.selectedValue);
  }

  get filteredOptions(): FilterOption[] {
    if (!this.searchText.trim()) return this.options;
    const query = this.searchText.toLowerCase().trim();
    return this.options.filter((opt) =>
      opt.label.toLowerCase().includes(query)
    );
  }

  isSelected(val: any): boolean {
    if (this.multiple) {
      return Array.isArray(this.tempSelectedValue) && this.tempSelectedValue.includes(val);
    }
    return this.tempSelectedValue === val;
  }

  get isApplyDisabled(): boolean {
    if (this.multiple) {
      return !Array.isArray(this.tempSelectedValue) || this.tempSelectedValue.length === 0;
    }
    return (
      this.tempSelectedValue === null ||
      this.tempSelectedValue === undefined ||
      this.tempSelectedValue === ''
    );
  }

  toggleDropdown(event?: Event): void {
    if (this.disabled) return;
    if (event) event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.tempSelectedValue = this.multiple
        ? (Array.isArray(this.selectedValue) ? [...this.selectedValue] : [])
        : this.selectedValue;
      this.searchText = '';
    }
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.onTouched();
  }

  selectOption(val: any): void {
    if (this.multiple) {
      if (!Array.isArray(this.tempSelectedValue)) {
        this.tempSelectedValue = [];
      }
      const idx = this.tempSelectedValue.indexOf(val);
      if (idx > -1) {
        this.tempSelectedValue.splice(idx, 1);
      } else {
        this.tempSelectedValue.push(val);
      }
    } else {
      this.tempSelectedValue = val;
    }
  }

  applyFilter(event?: Event): void {
    if (event) event.stopPropagation();
    this.selectedValue = this.multiple
      ? (Array.isArray(this.tempSelectedValue) ? [...this.tempSelectedValue] : [])
      : this.tempSelectedValue;
    this.onChange(this.selectedValue);
    this.filterChange.emit(this.selectedValue);
    this.closeDropdown();
  }

  handleCancelFilter(event: Event): void {
    event.stopPropagation();
    if (!this.isFilterApplied) return;
    this.selectedValue = this.multiple ? [] : null;
    this.tempSelectedValue = this.multiple ? [] : null;
    this.onChange(this.selectedValue);
    this.filterChange.emit(this.selectedValue);
    this.clear.emit();
    this.closeDropdown();
  }
}
